import {ActionContext, Module} from 'vuex'
import {AccessorHandler} from '@simpli/vuex-typescript'
import {AuthState, RootState} from '@/types/store'
import {AuthRequest} from '@/model/request/AuthRequest'
import {Global} from '@/model/resource/Global'
import {$} from '@/facade'

export type AuthContext = ActionContext<AuthState, RootState>

const TOKEN_STORAGE_KEY = 'ndapp.auth.token'

const readStoredToken = (): string | null =>
  typeof localStorage !== 'undefined'
    ? localStorage.getItem(TOKEN_STORAGE_KEY)
    : null

@AccessorHandler
export class AuthModule implements Module<AuthState, RootState> {
  namespaced = true

  state: AuthState = {
    token: readStoredToken(),
    user: null,
    cachePath: null,
    global: new Global(),
  }

  getters = {
    token: (state: AuthState) => state.token,
    isLogged: (state: AuthState) => Boolean(state.user || state.token),
    user: (state: AuthState) => state.user,
    global: (state: AuthState) => state.global,
  }

  actions = {
    async signIn(context: AuthContext, payload: {code: string; state: string}) {
      if (!payload?.code || !payload?.state)
        throw new Error('invalid_signin_payload')

      const {code, state} = payload

      const authResponse = await AuthRequest.signIn(code, state)

      if (authResponse?.token) {
        context.commit('POPULATE_TOKEN', authResponse.token)
        await $.nav.replace('/dashboard')
      } else {
        await $.nav.replace('/sign-in')
      }

      context.commit('POPULATE', authResponse)
      return authResponse
    },

    async authenticate(context: AuthContext) {
      try {
        const {user} = await AuthRequest.me()

        if (user) {
          context.commit('POPULATE', {user})
        }
      } catch (e) {
        console.error(e)
        context.commit('FORGET')
      }

      const global = await Global.getGlobal()
      context.commit('POPULATE_GLOBAL_SYSTEM_PREFERENCES', global)
      context.dispatch('walletAdapter/syncActiveSessions', null, {root: true})
    },

    async signOut(context: AuthContext) {
      try {
        context.commit('FORGET')
        await $.nav.replace('/sign-in')
      } catch (e) {
        console.error(e)
      }
    },

    async loadGlobal(context: AuthContext) {
      const global = await Global.getGlobal()
      context.commit('POPULATE_GLOBAL_SYSTEM_PREFERENCES', global)
      context.dispatch('walletAdapter/syncActiveSessions', null, {root: true})
    },
  }

  mutations = {
    POPULATE(state: AuthState, response: {user: any | null}) {
      state.user = response.user
    },

    POPULATE_TOKEN(state: AuthState, token: string | null) {
      state.token = token
      if (typeof localStorage !== 'undefined') {
        if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token)
        else localStorage.removeItem(TOKEN_STORAGE_KEY)
      }
    },

    POPULATE_GLOBAL_SYSTEM_PREFERENCES(state: AuthState, global: Global) {
      state.global = global
    },

    FORGET(state: AuthState) {
      state.token = null
      state.user = null
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(TOKEN_STORAGE_KEY)
      }
    },
  }
}
